import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash, FaBan } from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import EditSubAdminForm from "./EditSubAdminForm";
import { API_BASE_URL } from "../../../../config";

const AdminTable = ({ searchTerm }) => {
  const [admins, setAdmins] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSubAdminData, setEditSubAdminData] = useState(null);

  const fetchAdmins = async (page = 1) => {
    const loadingToast = toast.loading("Fetching subadmins...");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/get-all-subadmin?page=${page}`,
        { headers: { token } }
      );
      toast.dismiss(loadingToast);
      if (response.data.success) {
        setAdmins(response.data.result.subAdmins);
        setTotalPages(response.data.result.totalPages);
      } else {
        toast.error("Failed to fetch subadmins");
        setAdmins([]);
      }
    } catch {
      toast.dismiss(loadingToast);
      toast.error("Error fetching subadmins");
      setAdmins([]);
    }
  };

  useEffect(() => {
    fetchAdmins(page);
  }, [page]);

  const getStatusClass = (status) => {
    const s = status?.toLowerCase();
    if (s === "active") return "text-green-600 font-medium";
    if (["delete", "blocked", "block"].includes(s))
      return "text-red-600 font-medium";
    return "text-gray-600 font-medium";
  };

  const handleDelete = async () => {
    if (!selectedAdminId) return toast.error("Invalid subadmin ID");
    const subAdmin = admins.find((adm) => adm._id === selectedAdminId);
    if (!subAdmin) return toast.error("Subadmin not found");

    if (subAdmin.status?.toLowerCase() === "delete") {
      toast.warning("Subadmin is already deleted");
      setShowModal(false);
      setSelectedAdminId(null);
      return;
    }

    const loadingToast = toast.loading("Deleting subadmin...");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/delete-subadmin/${selectedAdminId}`,
        {},
        { headers: { token } }
      );
      toast.dismiss(loadingToast);
      if (response.data.success) {
        toast.success("Subadmin deleted successfully");
        fetchAdmins(page);
      } else {
        toast.error(response.data.message || "Failed to delete subadmin");
      }
    } catch {
      toast.dismiss(loadingToast);
      toast.error("Error deleting subadmin");
    } finally {
      setShowModal(false);
      setSelectedAdminId(null);
    }
  };

  const handleBlock = async () => {
    if (!selectedAdminId) return toast.error("Invalid subadmin ID");
    const subAdmin = admins.find((adm) => adm._id === selectedAdminId);
    if (!subAdmin) return toast.error("Subadmin not found");

    if (["block", "blocked"].includes(subAdmin.status?.toLowerCase())) {
      toast.info("Subadmin is already blocked");
      setShowBlockModal(false);
      setSelectedAdminId(null);
      return;
    }

    const loadingToast = toast.loading("Blocking subadmin...");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/block-subadmin/${selectedAdminId}`,
        {},
        { headers: { token } }
      );
      toast.dismiss(loadingToast);
      if (response.data.success) {
        toast.success("Subadmin blocked successfully");
        fetchAdmins(page);
      } else {
        toast.error(response.data.message || "Failed to block subadmin");
      }
    } catch {
      toast.dismiss(loadingToast);
      toast.error("Error blocking subadmin");
    } finally {
      setShowBlockModal(false);
      setSelectedAdminId(null);
    }
  };

  const filteredAdmins = admins.filter((admin) =>
    `${admin.adminName} ${admin.email}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative rounded-lg bg-white p-4 shadow dark:bg-navy-700">
      {/* <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-100 text-xs text-gray-800 dark:bg-gray-700 dark:text-white">
            <tr>
              <th className="whitespace-nowrap px-4 py-2">SI No</th>
              <th className="whitespace-nowrap px-4 py-2">Name</th>
              <th className="whitespace-nowrap px-4 py-2">Email</th>
              <th className="whitespace-nowrap px-4 py-2">Role</th>
              <th className="whitespace-nowrap px-4 py-2">Status</th>
              <th className="whitespace-nowrap px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAdmins.length ? (
              filteredAdmins.map((admin, idx) => (
                <tr
                  key={admin._id}
                  className="hover:bg-gray-50 dark:text-white dark:hover:bg-navy-800"
                >
                  <td className="px-4 py-2">{(page - 1) * 10 + idx + 1}</td>
                  <td className="px-4 py-2">{admin.adminName}</td>
                  <td className="px-4 py-2">{admin.email}</td>
                  <td className="px-4 py-2">
                    {admin.isSuperAdmin ? "Super Admin" : "Sub Admin"}
                  </td>
                  <td className={`px-4 py-2 ${getStatusClass(admin.status)}`}>
                    {admin.status}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          setEditSubAdminData(admin);
                          setShowEditModal(true);
                        }}
                        className="text-green-600 hover:text-green-800"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedAdminId(admin._id);
                          setShowModal(true);
                        }}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedAdminId(admin._id);
                          setShowBlockModal(true);
                        }}
                        className="text-yellow-600 hover:text-yellow-800"
                        title="Block"
                      >
                        <FaBan />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="py-4 text-center text-gray-600">
                  No admins found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div> */}

      <div className="max-h-[500px] overflow-x-auto overflow-y-auto">
        <table className="min-w-full border border-gray-300 text-left text-sm">
          <thead className="sticky top-0 z-10 bg-gray-100 text-xs text-gray-800 dark:bg-gray-700 dark:text-white">
            <tr>
              <th className="whitespace-nowrap border border-gray-300 px-4 py-2">
                SI No
              </th>
              <th className="whitespace-nowrap border border-gray-300 px-4 py-2">
                Name
              </th>
              <th className="whitespace-nowrap border border-gray-300 px-4 py-2">
                Email
              </th>
              <th className="whitespace-nowrap border border-gray-300 px-4 py-2">
                Role
              </th>
              <th className="whitespace-nowrap border border-gray-300 px-4 py-2">
                Status
              </th>
              <th className="whitespace-nowrap border border-gray-300 px-4 py-2">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAdmins.length ? (
              filteredAdmins.map((admin, idx) => (
                <tr
                  key={admin._id}
                  className="hover:bg-gray-50 dark:text-white dark:hover:bg-navy-800"
                >
                  <td className="border border-gray-300 px-4 py-2">
                    {(page - 1) * 10 + idx + 1}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {admin.adminName}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {admin.email}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {admin.isSuperAdmin ? "Super Admin" : "Sub Admin"}
                  </td>
                  <td
                    className={`border border-gray-300 px-4 py-2 ${getStatusClass(
                      admin.status
                    )}`}
                  >
                    {admin.status}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <div className="flex flex-wrap gap-2">
                      {/* Edit */}
                      <button
                        onClick={() => {
                          setEditSubAdminData(admin);
                          setShowEditModal(true);
                        }}
                        className="text-green-600 hover:text-green-800"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      {/* Delete */}
                      <button
                        onClick={() => {
                          setSelectedAdminId(admin._id);
                          setShowModal(true);
                        }}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                      {/* Block */}
                      <button
                        onClick={() => {
                          setSelectedAdminId(admin._id);
                          setShowBlockModal(true);
                        }}
                        className="text-yellow-600 hover:text-yellow-800"
                        title="Block"
                      >
                        <FaBan />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="6"
                  className="border border-gray-300 py-4 text-center text-gray-600"
                >
                  No admins found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 1}
            className="rounded border px-3 py-1 text-sm disabled:opacity-50 dark:text-white"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700 dark:text-white">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page === totalPages}
            className="rounded border px-3 py-1 text-sm disabled:opacity-50 dark:text-white"
          >
            Next
          </button>
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <ConfirmationModal
          message="Are you sure you want to Delete?"
          onCancel={() => setShowModal(false)}
          onConfirm={handleDelete}
          confirmLabel="Delete"
          confirmClass="bg-red-600 hover:bg-red-700"
        />
      )}

      {showBlockModal && (
        <ConfirmationModal
          message="Are you sure you want to Block?"
          onCancel={() => setShowBlockModal(false)}
          onConfirm={handleBlock}
          confirmLabel="Block"
          confirmClass="bg-yellow-600 hover:bg-yellow-700"
        />
      )}

      {showEditModal && editSubAdminData && (
        <EditSubAdminForm
          onClose={() => {
            setShowEditModal(false);
            setEditSubAdminData(null);
            fetchAdmins(page);
          }}
          subAdminData={editSubAdminData}
        />
      )}
    </div>
  );
};

// Reusable confirmation modal
const ConfirmationModal = ({
  message,
  onCancel,
  onConfirm,
  confirmLabel,
  confirmClass,
}) => (
  <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <div className="w-[90%] max-w-sm rounded-lg bg-white p-6 shadow-lg dark:bg-navy-800">
      <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white">
        {message}
      </h2>
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="rounded-md border px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-white dark:hover:bg-white/20"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className={`rounded-md px-4 py-2 text-sm text-white ${confirmClass}`}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

export default AdminTable;
